/**
 * The case's live cameras for the Monitor's Cameras tab: read every few seconds (their pictures
 * and states change while the agent records), and the writes a collaborator may make. The member
 * role comes with the watchlists the page reads anyway.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { CameraActions, CamerasPaneProps } from '@/components/monitor/CamerasPane'
import { type CameraCreate, type CameraUpdate, camerasApi } from '@/services/api/case-cameras'
import { canChangeWatching, toLoadView } from './monitor-rows'
import { useWatchlists } from './useMonitor'

const camerasKey = (caseId: string) => ['case', caseId, 'cameras'] as const

/** How often the cards read their cameras and pictures again. */
const REFRESH_MS = 3000

export function useCamerasPane(
  caseId: string,
  onOpenDocument: (documentId: string) => void
): CamerasPaneProps {
  const client = useQueryClient()
  const watchlists = useWatchlists(caseId)
  const [failed, setFailed] = useState(false)
  const cameras = useQuery({
    queryKey: camerasKey(caseId),
    queryFn: () => camerasApi.list(caseId),
    enabled: Boolean(caseId),
    refetchInterval: REFRESH_MS,
  })
  const done = {
    onSuccess: () => {
      setFailed(false)
      void client.invalidateQueries({ queryKey: camerasKey(caseId) })
    },
    onError: () => setFailed(true),
  }
  const create = useMutation({
    mutationFn: (body: CameraCreate) => camerasApi.create(caseId, body),
    ...done,
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CameraUpdate }) =>
      camerasApi.update(caseId, id, body),
    ...done,
  })
  const remove = useMutation({ mutationFn: (id: string) => camerasApi.remove(caseId, id), ...done })
  const actions: CameraActions = {
    create: body => create.mutateAsync(body).then(() => undefined),
    setEnabled: (id, enabled) => update.mutate({ id, body: { enabled } }),
    remove: id => remove.mutate(id),
    pending: create.isPending || update.isPending || remove.isPending,
    failed,
  }
  return {
    view: toLoadView(cameras, data => data.cameras),
    snapshotUrl: (cameraId, tick) => camerasApi.snapshotUrl(caseId, cameraId, tick),
    refreshMs: REFRESH_MS,
    actions: canChangeWatching(watchlists.data?.member_role) ? actions : null,
    onOpenDocument,
    onRetry: () => void cameras.refetch(),
  }
}
